import React, { PureComponent } from 'react';

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell
} from "recharts";
import { Loader } from 'react-loaders';

import { batchRequest, getRequest } from '../../../../../../api/ApiService';

import {
  Wrapper,
  PreTitle,
  Title,
  Text,
  ContentContainer,
  ContentTitle,
  ContentFlexColumns,
  ContentRightColumn,
  ChartDataLabel
} from '../../../components/ui';

import DataDetails from '../../../components/data-details';

const chartColors = [
  '#81c991',
  '#fd9595'
]

class Inclusiveness extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      studentsFeltIncluded: [],
      studentsFeltNotIncluded: [],
      extraDetailsTitle: 'Felt Included',
      extraDetailsColor: chartColors[0],
      extraDetailsData: [],
      loaded: false
    };
  }

  componentDidMount() {
    const { currentFinishedSurvey, surveysStatsByUser } = this.props;
    batchRequest([
      `${process.env.REACT_APP_API_URL}users`,
      `${process.env.REACT_APP_API_URL}v2/forms/answers-list/?survey=${currentFinishedSurvey.id}`,
    ])
      .then(async res => {
        const users = res[0].body;

        let formsAnswersList = res[1].body.results;
        const nextPageAvailable = res[1].body.next;
        if (nextPageAvailable) {
          const newFormsAnswersList = await this.fetchFormsAnswersList(nextPageAvailable);
          formsAnswersList.push(...newFormsAnswersList);
        }

        const answersWithCriteria = formsAnswersList.filter(formsAnswer => {
          const user = users.find(user => user.email === formsAnswer.giver_email);

          return formsAnswer.question_category === 'Self assessment'
            && formsAnswer.question_text.toLowerCase().includes('inclusiveness')
            && surveysStatsByUser.find(userStats => userStats.user_id === user.id).evaluation_completed
        });

        let uniqueCriteriaUsers = [];
        let feltIncluded = answersWithCriteria.filter(answerWithCriteria => {
          if (!uniqueCriteriaUsers.includes(answerWithCriteria.giver_email)) {
            const currentAnswerGiverEmail = answerWithCriteria.giver_email;
            uniqueCriteriaUsers.push(currentAnswerGiverEmail);
            const allUserAnswers = answersWithCriteria.filter(answerWithCriteria => answerWithCriteria.giver_email === currentAnswerGiverEmail);
            return allUserAnswers.every(userAnswer => Number(userAnswer.answer_text) >= 4);
          }
          return false;
        });

        const studentsFeltIncluded = feltIncluded.map(item => {
          const user = users.find(user => user.email === item.giver_email);
          return `${user.first_name} ${user.last_name}`
        });

        uniqueCriteriaUsers = [];
        const feltNotIncluded = answersWithCriteria.filter(answerWithCriteria => {
          if (!uniqueCriteriaUsers.includes(answerWithCriteria.giver_email)) {
            const currentAnswerGiverEmail = answerWithCriteria.giver_email;
            uniqueCriteriaUsers.push(currentAnswerGiverEmail);
            const allUserAnswers = answersWithCriteria.filter(answerWithCriteria => answerWithCriteria.giver_email === currentAnswerGiverEmail);
            return allUserAnswers.every(userAnswer => Number(userAnswer.answer_text) < 3);
          }
          return false;
        });

        const studentsFeltNotIncluded = feltNotIncluded.map(item => {
          const user = users.find(user => user.email === item.giver_email);
          return `${user.first_name} ${user.last_name}`
        });

        this.setState({
          studentsFeltIncluded,
          studentsFeltNotIncluded,
          extraDetailsData: studentsFeltIncluded,
          loaded: true
        });
      })
      .catch(err => { });
  }

  fetchFormsAnswersList = async (nextPage) => {
    let nextPageAvailable = true;
    let formsAnswersList = [];
    let nextPageUrl = nextPage;

    while (nextPageAvailable) {
      nextPageAvailable = false;
      await getRequest(
        nextPageUrl
      )
        // eslint-disable-next-line no-loop-func
        .then(res => {
          nextPageUrl = res.body.next;
          nextPageAvailable = nextPageUrl;
          formsAnswersList.push(...res.body.results);
        })
        .catch(err => { });
    }

    return formsAnswersList;
  }

  changeExtraDetails = (title, color, data) => {
    this.setState({
      extraDetailsTitle: title,
      extraDetailsColor: color,
      extraDetailsData: data
    });
  }

  render() {
    const { gamesTeamplayers } = this.props;
    const {
      studentsFeltIncluded,
      studentsFeltNotIncluded,
      extraDetailsTitle,
      extraDetailsColor,
      extraDetailsData,
      loaded
    } = this.state;

    const studentsFeltIncludedCount = studentsFeltIncluded.length;
    const studentsFeltNotIncludedCount = studentsFeltNotIncluded.length;

    const chartData = [
      {
        value: studentsFeltIncludedCount,
        color: chartColors[0],
        title: 'Felt Included'
      },
      {
        value: studentsFeltNotIncludedCount,
        color: chartColors[1],
        title: 'Did Not Feel Included'
      }
    ];

    return loaded ? (
      <Wrapper>
        <PreTitle>Individual</PreTitle>
        <Title>Inclusiveness</Title>
        <Text>
          <p>in terms of feeling included, <b>{studentsFeltIncludedCount}</b> out of <b>{gamesTeamplayers.length}</b> students felt heard and included in team discussions.</p>
          <br />
          <p><strong>NOT Inclusive</strong></p>
          <p>However <b>{studentsFeltNotIncludedCount}</b> students felt that they were not heard and/or included in team discussions.</p>
        </Text>
        <ContentContainer>
          <ContentTitle>Individual Contributions</ContentTitle>
          <ContentFlexColumns withFloatinDetailsBlock={true} withPadding={true}>
            <ResponsiveContainer width={170} height={160}>
              <BarChart
                data={chartData}
                margin={{ top: 32, left: 10, right: 20, bottom: 38 }}
              >
                <Bar
                  barSize={32}
                  dataKey="value"
                >
                  {
                    chartData.map((item, index) => (
                      <Cell key={index} fill={item.color} />
                    ))
                  }
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <ContentRightColumn>
              <ChartDataLabel
                onClick={() => this.changeExtraDetails(chartData[0].title, chartColors[0], studentsFeltIncluded)}
              >
                {`${chartData[0].title} (${studentsFeltIncludedCount})`}
              </ChartDataLabel>
              <ChartDataLabel
                color={chartColors[1]}
                onClick={() => this.changeExtraDetails(chartData[1].title, chartColors[1], studentsFeltNotIncluded)}
              >
                {`${chartData[1].title} (${studentsFeltNotIncludedCount})`}
              </ChartDataLabel>
            </ContentRightColumn>
          </ContentFlexColumns>
          <DataDetails
            title={extraDetailsTitle}
            color={extraDetailsColor}
            data={extraDetailsData}
          />
        </ContentContainer>
      </Wrapper>
    ) : <Loader type="ball-scale-ripple" active />;
  }

}

export default Inclusiveness;



// WEBPACK FOOTER //
// ./src/instructor/components/peer-feedback/pages/dynamics/individual/inclusiveness.js